import InstallOssDeviceModal from "@/components/hardware/install/install-oss-device/InstallOssDeviceModal";
import OssDeviceTab from "@/containers/oss-device-tab";
import { FC, ReactElement, ReactNode, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useGlobalState } from "../../../Providers";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import { useDialog } from "../../../components/dialog/DialogContext";
import InstallBiometricModal from "../../../components/hardware/install/installBiometric/InstallBiometricModal";
import InstallMasterControllerModal from "../../../components/hardware/install/installMasterController/InstallMasterControllerModal";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import { isCloud } from "../../../constants/license-constants";
import { DEVICE_TYPE_CATEGORY, getCountsCategory, TYPE_PER_DEVICE_TYPE } from "../../../constants/system-constants";
import BiometricTab from "../../../containers/biometrics-tab";
import IOModulesTab from "../../../containers/io-module-tab";
import LocksTab from "../../../containers/locks-tab";
import MasterControllerTab from "../../../containers/master-controller-tab";
import ReaderTab from "../../../containers/reader-tab";
import Restricted from "../../../contexts/PermissionProvider/RestrictedWrapper";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { useIcaWebsocketContext } from "../../../contexts/websocket/IcaWebsocketContext";
import { Button } from "../../../components/button/Button";
import plusIcon from "../../../images/Plus.svg";
import { Category } from "../../../models/Category";
import { getSystemStats } from "../../../services/systemService";
import { showError } from "../../../services/utilities/ServiceError";
import { LICENSE_APERIO_OSS } from "../../../constants/license-constants";
import SystemStat from "@/models/SystemStat";

type HardwareProps = {
    selected: number;
    setSelected: (index: number) => void;
}

const Hardware: FC<HardwareProps> = ({ selected, setSelected }) => {
    const { buildInfo } = useGlobalState() ?? {};
    const isLicensedForAperioOss = buildInfo?.licenceIds?.includes(LICENSE_APERIO_OSS);
    const [busy, setBusy] = useState(false);
    const history = useHistory();
    const [categories, setCategories] = useState<Category[]>([]);
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const { translate } = useLocale();
    const [installMasterController, setInstallMasterControllerModal] = useState(false);
    const [installBiometricModal, setInstallBiometricModal] = useState(false);
    const [installOssDeviceModal, setInstallOssDeviceModal] = useState(false);
    const [busyMessage, setBusyMessage] = useState<string>('');
    const icaWebSocket = useIcaWebsocketContext();
    const dialog = useDialog();

    const isCloudMode = useMemo(() => {
        return isCloud(buildInfo?.usage);
    }, [buildInfo?.usage]);

    const buildCategoriesData = async () => {
        try {
            setBusy(true);
            const showTabCounts = window.sessionStorage.getItem("showTabCounts") === "1";
            const systemStats: SystemStat | undefined = showTabCounts ? await getSystemStats(TYPE_PER_DEVICE_TYPE) : undefined;

            setCategories([
                {
                    displayName: translate.byKey(TranslationKeys.masterControllers),
                    heading: translate.byKey(TranslationKeys.masterControllers),
                    value: "",
                    id: 0,
                    count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.CONTOLLERS) : 0
                },
                (!isCloudMode && (
                    {
                        displayName: translate.byKey(TranslationKeys.biometrics),
                        heading: translate.byKey(TranslationKeys.biometrics),
                        value: "",
                        id: 1,
                        count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.BIOMETRICS) : 0
                    })
                ),
                {
                    displayName: translate.byKey(TranslationKeys.readers),
                    heading: translate.byKey(TranslationKeys.readers),
                    value: "",
                    id: 2,
                    count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.READERS) : 0
                },
                {
                    displayName: translate.byKey(TranslationKeys.modulesAndIO),
                    heading: translate.byKey(TranslationKeys.modulesAndIO),
                    value: "",
                    id: 3,
                    count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.IO_MODULES) : 0
                },
                {
                    displayName: translate.byKey(TranslationKeys.locks),
                    heading: translate.byKey(TranslationKeys.locks),
                    value: "",
                    id: 4,
                    count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.LOCKS) : 0
                },
                (isLicensedForAperioOss && (
                    {
                        displayName: translate.byKey(TranslationKeys.ossDevices),
                        heading: translate.byKey(TranslationKeys.ossDevices),
                        value: "",
                        id: 5,
                        count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.OSS_DEVICES) : 0,
                    })
                ),
            ].filter(Boolean) as Category[]);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        buildCategoriesData();
    }, [isCloudMode]);

    useEffect(() => {
        if (categories.length > 0) {
            const newTabContent: ReactElement[] = [
                <MasterControllerTab
                    setShowInstallMasterControllerModal={setInstallMasterControllerModal}
                    categories={categories}
                    setBusy={setBusy}
                    setCategories={setCategories}
                    setBusyMessage={setBusyMessage} />,
                (!isCloudMode && (
                    <BiometricTab
                        setShowInstallBiometricModal={setInstallBiometricModal}
                        setBusy={setBusy}
                        categories={categories}
                        setCategories={setCategories}
                        setBusyMessage={setBusyMessage} />
                )),
                <ReaderTab
                    categories={categories}
                    setBusy={setBusy}
                    setCategories={setCategories}
                    setBusyMessage={setBusyMessage} />,
                <IOModulesTab
                    categories={categories}
                    setBusy={setBusy}
                    setCategories={setCategories}
                    setBusyMessage={setBusyMessage} />,
                <LocksTab
                    categories={categories}
                    setBusy={setBusy}
                    setCategories={setCategories}
                    setBusyMessage={setBusyMessage}
                />,
                (isLicensedForAperioOss && (
                    <OssDeviceTab
                        categories={categories}
                        setBusy={setBusy}
                        setCategories={setCategories}
                        setShowModal={setInstallOssDeviceModal}
                        setBusyMessage={setBusyMessage} />
                ))
                // <LockersTab/>
            ].filter(Boolean) as ReactElement[];
            setTabContent(newTabContent);
        }
    }, [categories, isCloudMode]);

    const showInstallModal = () => {
        if (!icaWebSocket.connectionState && selected !== 5) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.installConnectionError) });
            return;
        }

        switch (selected) {
            case 0:
                setInstallMasterControllerModal(true);
                break;
            case 1:
                setInstallBiometricModal(true);
                break;
            case 5:
                setInstallOssDeviceModal(true);
                break;
            default:
                break;
        }
    }

    const postInstall = async () => {
        try {
            setBusy(true);
            const systemStats = buildInfo?.showTabCounts ? await getSystemStats(TYPE_PER_DEVICE_TYPE) : undefined;

            const newCatergories: Category[] = categories.map((category) => {
                if (category.id === 0) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.CONTOLLERS) : 0 };
                }
                else if (category.id === 1) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.BIOMETRICS) : 0 };
                }
                else if (category.id === 2) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.READERS) : 0 };
                }
                else if (category.id === 3) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.IO_MODULES) : 0 };
                }
                else if (category.id === 4) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.LOCKS) : 0 };
                }
                else if (category.id === 5) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.OSS_DEVICES) : 0 };
                }

                return category;
            })
            setCategories(newCatergories)
            setBusy(false);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    useEffect(() => {
        if (!busy) {
            setBusyMessage('');
        }
    }, [busy])

    return <ActivityFrame busy={busy} className={`flex w-full h-full justify-center ${busy ? 'flex-wrap content-center' : ''}`} busyMessage={busyMessage}>
        <div id="container-tabs" className="pt-4 pl-4 pb-4 flex flex-col flex-grow ">
            <Breadcrumb
                path={[
                    {
                        value: "/access-control/hardware",
                        displayName: translate.byKey(TranslationKeys.hardware),
                    }
                ]}
                onClick={(item) => history.push(item.value)} />
            <Tabs
                className="bg-white"
                tabs={getTabsListFromLabels(categories)}
                onSelect={(i) => setSelected(i)}
                selected={selected}
                onAdd={() => { }}
                add={false}
                tabStyle={{}}>
                {tabContent[selected]}
            </Tabs>
            <div className="flex flex-row justify-end button-cluster">
                {[0, 1, 5].includes(selected) ?
                    <Restricted to='add.hardware.element'>
                        <Button
                            variant="submit"
                            shape="rounded"
                            id="installButton"
                            onClick={showInstallModal}>
                            <img
                                src={plusIcon}
                                className="px-2 w-8 py-1"
                                alt="" />
                            <span className="mr-2">{translate.byKey(TranslationKeys.install)}</span>
                        </Button>
                    </Restricted> : null}
            </div>
            <InstallMasterControllerModal
                isOpen={installMasterController}
                postInstall={postInstall}
                onClose={() => { setInstallMasterControllerModal(false) }} />
            <InstallBiometricModal
                isOpen={installBiometricModal}
                postInstall={postInstall}
                onClose={() => { setInstallBiometricModal(false) }} />
            <InstallOssDeviceModal
                isOpen={installOssDeviceModal}
                onClose={() => setInstallOssDeviceModal(false)}
                postInstall={postInstall} />
        </div>
    </ActivityFrame>
};

export default Hardware;
